import { z } from 'zod';

/**
 * Lead lifecycle statuses.
 */
export const LeadStatusSchema = z.enum([
  'new',
  'enriching',
  'enriched',
  'scoring',
  'scored',
  'qualifying',
  'qualified',
  'nurturing',
  'contacted',
  'engaged',
  'delivered',
  'converted',
  'disqualified',
  'do_not_contact',
]);

export type LeadStatus = z.infer<typeof LeadStatusSchema>;

/**
 * Valid status transitions for the lead lifecycle state machine.
 * Each key is a status, and the value is the list of statuses it can transition to.
 *
 * NOTE: `do_not_contact` is a valid target from EVERY status. This is a compliance
 * override: when a person is added to the DNC list, it is a legal requirement that
 * takes precedence over the normal lifecycle. Even `converted` leads must be
 * transitioned so future outreach is prevented.
 */
export const VALID_TRANSITIONS: Record<LeadStatus, readonly LeadStatus[]> = {
  new: [
    'enriching',
    'enriched',
    'scoring',
    'scored',
    'disqualified',
    'do_not_contact',
  ],
  enriching: ['enriched', 'disqualified', 'do_not_contact'],
  enriched: ['scoring', 'scored', 'disqualified', 'do_not_contact'],
  scoring: ['scored', 'disqualified', 'do_not_contact'],
  scored: [
    'qualifying',
    'qualified',
    'nurturing',
    'disqualified',
    'do_not_contact',
  ],
  qualifying: ['qualified', 'nurturing', 'disqualified', 'do_not_contact'],
  qualified: ['contacted', 'delivered', 'disqualified', 'do_not_contact'],
  nurturing: ['qualified', 'contacted', 'disqualified', 'do_not_contact'],
  contacted: ['engaged', 'nurturing', 'disqualified', 'do_not_contact'],
  engaged: ['delivered', 'nurturing', 'do_not_contact'],
  delivered: ['converted', 'disqualified', 'do_not_contact'],
  converted: ['do_not_contact'],
  disqualified: ['new', 'do_not_contact'],
  do_not_contact: [],
} as const;

/**
 * Check whether a status transition is allowed by the state machine.
 */
export function isValidTransition(from: LeadStatus, to: LeadStatus): boolean {
  const allowed = VALID_TRANSITIONS[from];
  return allowed.includes(to);
}

/**
 * Lead acquisition sources.
 */
export const LeadSourceSchema = z.enum([
  'landing_page',
  'meta_ads',
  'google_maps',
  'csv_import',
  'manual',
  'referral',
  'api',
  'whatsapp_agent',
  'web_widget',
  // Native ad-platform lead-form sources (parallel to 'meta_ads'). The DB enum
  // values were added in 20260605120000_add_ad_platform_lead_sources.sql; keep
  // this TS enum in sync so CreateLead validation accepts webhook-originated
  // leads tagged with the platform.
  'linkedin_ads',
  'tiktok_ads',
  'google_ads',
]);

export type LeadSource = z.infer<typeof LeadSourceSchema>;

/**
 * Core lead schema with standard fields shared across all verticals.
 */
export const LeadSchema = z.object({
  id: z.string(),
  accountId: z.string().uuid(),
  verticalId: z.string(),
  email: z.string().email().optional(),
  phone: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  status: LeadStatusSchema,
  score: z.number().nullable(),
  source: LeadSourceSchema,
  campaign: z.string().optional(),
  customFields: z.record(z.string(), z.unknown()),
  consentGiven: z.boolean(),
  consentTimestamp: z.string().datetime().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Lead = z.infer<typeof LeadSchema>;

/**
 * Input schema for creating a new lead.
 */
/**
 * Normalize a name-like string field on Zod input: trim whitespace and
 * map empty strings to `undefined` so they are never persisted as `""`.
 *
 * Empty-string first/last names were the dominant driver-vertical delivery
 * failure ("Lead is missing required fields: phone, first_name, or
 * last_name") because the Uber Supplier Portal check `!firstName || !lastName`
 * rejects `""` but the rows had passed earlier truthy gates elsewhere.
 */
const normalizeNameField = (v: unknown) => {
  if (typeof v !== 'string') return v;
  const trimmed = v.trim();
  return trimmed.length === 0 ? undefined : trimmed;
};

export const CreateLeadSchema = z.object({
  accountId: z.string().uuid(),
  verticalId: z.string(),
  email: z.string().email().optional(),
  phone: z.string().optional(),
  firstName: z.preprocess(normalizeNameField, z.string().min(1).optional()),
  lastName: z.preprocess(normalizeNameField, z.string().min(1).optional()),
  source: LeadSourceSchema,
  campaign: z.string().optional(),
  customFields: z.record(z.string(), z.unknown()).default({}),
  consentGiven: z.boolean(),
  utmSource: z.string().optional(),
  utmMedium: z.string().optional(),
  utmCampaign: z.string().optional(),
  utmContent: z.string().optional(),
  utmTerm: z.string().optional(),
  fbclid: z.string().optional(),
  gclid: z.string().optional(),
  experimentVariant: z.string().optional(),
  /**
   * Stable per-conversation id for web-widget leads (the ChatSuite
   * conversationId). Used as the dedup/merge key when there is no phone, so
   * the agent's field-by-field captures land on one lead. NULL for WhatsApp
   * and form leads.
   */
  webSessionId: z.string().max(255).optional(),
  /**
   * Meta Lead Ads leadgen_id. When present, used for webhook idempotency:
   * a duplicate webhook call for the same leadgen_id resolves to the
   * existing lead rather than erroring.
   */
  metaLeadgenId: z.string().optional(),
  /**
   * LinkedIn Lead Gen Form response id (urn tail). When present, used for
   * webhook idempotency the same way metaLeadgenId is: a duplicate
   * leadNotifications delivery for the same response id resolves to the
   * existing lead rather than creating a new one. Stored on leads.linkedin_lead_id
   * (unique partial index ux_leads_linkedin_lead_id).
   */
  linkedinLeadId: z.string().optional(),
  /**
   * Meta Click-to-WhatsApp click id from the WhatsApp referral payload.
   * Echoed back on CAPI Lead/Purchase events to close the CTWA loop.
   */
  ctwaClid: z.string().optional(),
  /** Meta ad id attributed to the lead (flat column mirror of custom_fields.meta_ad_id). */
  sourceAdId: z.string().optional(),
  /** Meta _fbp cookie captured at form submit. Sent with CAPI user_data.fbp. */
  fbp: z.string().optional(),
  /**
   * Capture-time context used to raise CAPI EMQ on downstream conversions:
   *   - clientIp, clientUserAgent
   *   - city (ct), state/region (st), zipCode (zp), country
   *   - eventSourceUrl (the page URL the user submitted from)
   * Unused keys are ignored. Stored as leads.meta_capture JSONB.
   */
  metaCapture: z.record(z.string(), z.unknown()).optional(),
});

export type CreateLead = z.infer<typeof CreateLeadSchema>;

/**
 * Interaction types for tracking lead engagement.
 */
export const InteractionTypeSchema = z.enum([
  'email_sent',
  'email_opened',
  'email_clicked',
  'sms_sent',
  'sms_replied',
  'whatsapp_sent',
  'whatsapp_replied',
  'call_made',
  'call_answered',
  'form_submitted',
  'page_visited',
  'meeting_scheduled',
  'note_added',
]);

export type InteractionType = z.infer<typeof InteractionTypeSchema>;

/**
 * Lead interaction record for tracking all touchpoints.
 */
export const LeadInteractionSchema = z.object({
  id: z.string(),
  leadId: z.string(),
  type: InteractionTypeSchema,
  channel: z.string().optional(),
  content: z.string().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  createdAt: z.string().datetime(),
});

export type LeadInteraction = z.infer<typeof LeadInteractionSchema>;
